import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, Tooltip, XAxis } from "recharts";
import { TrendingUp, ShoppingBag, AlertTriangle, Pizza, Power, Store, CalendarClock, Check, CheckCheck, Ticket } from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";

export default function MerchantDashboard() {
    const [s, setS] = useState(null);
    const [rests, setRests] = useState([]);
    const [today, setToday] = useState(null);

    const load = async () => {
        const [a, b, c] = await Promise.all([
            api.get("/merchant/dashboard").catch(() => ({ data: null })),
            api.get("/merchant/restaurants").catch(() => ({ data: [] })),
            api.get("/merchant/today-bookings").catch(() => ({ data: null })),
        ]);
        setS(a.data); setRests(b.data || []); setToday(c.data);
    };
    useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, []);

    const toggle = async (rid) => {
        try {
            await api.post(`/merchant/restaurants/${rid}/toggle`);
            toast.success("Status updated");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const setBookingStatus = async (bid, status) => {
        try {
            await api.patch(`/merchant/bookings/${bid}/status`, { status });
            toast.success(`Marked ${status}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const cards = [
        { label: "Revenue today", v: s ? inr(s.revenue_today) : "…", Icon: TrendingUp, accent: "bg-emerald-100 text-emerald-700" },
        { label: "Orders today", v: s?.orders_today ?? "…", Icon: ShoppingBag, accent: "bg-blue-100 text-blue-700" },
        { label: "Active orders", v: s?.active_orders ?? "…", Icon: ShoppingBag, accent: "bg-amber-100 text-amber-700" },
        { label: "Revenue (7d)", v: s ? inr(s.revenue_week) : "…", Icon: TrendingUp, accent: "bg-violet-100 text-violet-700" },
        { label: "Out of stock", v: s?.out_of_stock ?? "…", Icon: AlertTriangle, accent: "bg-red-100 text-red-700" },
        { label: "Low stock (≤5)", v: s?.low_stock ?? "…", Icon: Pizza, accent: "bg-orange-100 text-orange-700" },
    ];

    // iter43b: in-store coupon footfall (30d). Rendered as its own
    // emphasised tile because it ties two halves of the loyalty loop
    // — "did customers care?" (mints) and "did they actually walk in?"
    // (redemptions). One Link → merchant coupons page so the staff can
    // open the redemption panel directly.
    const unlockTile = {
        redeemed: s?.unlocks_30d_redeemed ?? null,
        minted: s?.unlocks_30d_minted ?? null,
        rate: s?.unlock_redemption_rate_pct ?? null,
        spark: Array.isArray(s?.unlock_sparkline_30d) ? s.unlock_sparkline_30d : [],
    };

    return (
        <div>
            <h1 className="text-2xl font-bold tracking-tight">Merchant Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">{rests.length} {rests.length === 1 ? "shop" : "shops"} under your account</p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                {cards.map((c) => (
                    <div key={c.label} data-testid={`metric-${c.label}`} className="bg-card border border-border rounded-2xl p-5">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.accent}`}><c.Icon size={18} /></div>
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold mt-4">{c.label}</p>
                        <p className="text-2xl font-bold mt-1">{c.v}</p>
                    </div>
                ))}
            </div>

            {/* In-store unlock footfall (last 30 days). Surfaces both
                customer interest (mints) and actual store visits
                (redeemed) so merchants can see if their coupons are
                driving real-world footfall — not just clicks. */}
            <Link
                to="/merchant/coupons"
                data-testid="metric-unlock-footfall"
                className="mt-4 group flex flex-col sm:flex-row gap-4 sm:items-stretch bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border border-amber-300/60 rounded-2xl p-5 shadow-sm active:scale-[0.997] transition"
            >
                <div className="flex items-stretch gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-amber-500 text-white inline-flex items-center justify-center shrink-0 shadow">
                        <Ticket size={22} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">In-store coupon footfall · last 30d</p>
                        <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 mt-1">
                            <span>
                                <span className="text-2xl font-bold" data-testid="unlock-footfall-redeemed">{unlockTile.redeemed ?? "…"}</span>
                                <span className="text-[11px] text-muted-foreground ml-1">redeemed</span>
                            </span>
                            <span>
                                <span className="text-xl font-bold" data-testid="unlock-footfall-minted">{unlockTile.minted ?? "…"}</span>
                                <span className="text-[11px] text-muted-foreground ml-1">unlocked</span>
                            </span>
                            {unlockTile.rate != null && (
                                <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5" data-testid="unlock-footfall-rate">
                                    {unlockTile.rate}% redemption rate
                                </span>
                            )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Tap to redeem a customer's code or view recent unlocks →
                        </p>
                    </div>
                </div>
                {/* Sparkline of daily REDEEMED counts. Hidden on
                    micro-screens to keep the tile compact; appears
                    inline at >= sm. */}
                <div className="hidden sm:block w-44 self-stretch" data-testid="unlock-footfall-sparkline">
                    {unlockTile.spark.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%" minHeight={56}>
                            <LineChart data={unlockTile.spark} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                                <XAxis dataKey="date" hide />
                                <Tooltip
                                    cursor={{ stroke: "#fbbf24", strokeWidth: 1 }}
                                    contentStyle={{ fontSize: 11, padding: "4px 8px", borderRadius: 6 }}
                                    labelFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                                    formatter={(v) => [v, "redeemed"]}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="count"
                                    stroke="#d97706"
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 3, fill: "#d97706" }}
                                    isAnimationActive={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-[10px] text-muted-foreground">
                            No redemptions yet
                        </div>
                    )}
                </div>
            </Link>

            <h2 className="text-lg font-bold mt-10 mb-3 inline-flex items-center gap-2"><CalendarClock size={18} /> Today's bookings</h2>
            <TodayBookings data={today} onAction={setBookingStatus} />

            <h2 className="text-lg font-bold mt-10 mb-3 inline-flex items-center gap-2"><Store size={18} /> Your shops</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {rests.map((r) => (
                    <div key={r.id} data-testid={`rest-card-${r.id}`} className="bg-card border border-border rounded-2xl p-4">
                        <p className="font-bold text-sm">{r.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{(r.cuisines || []).join(" • ")}</p>
                        <div className="flex items-center justify-between mt-3">
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${(!r.manual_closed && r.is_open) ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                                {(!r.manual_closed && r.is_open) ? "Open" : "Paused"}
                            </span>
                            <button data-testid={`toggle-${r.id}`} onClick={() => toggle(r.id)} className="text-xs font-semibold text-primary inline-flex items-center gap-1"><Power size={12} /> {r.manual_closed ? "Resume" : "Pause"}</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

const PILL = {
    PENDING: "bg-amber-100 text-amber-800",
    CONFIRMED: "bg-emerald-100 text-emerald-800",
    COMPLETED: "bg-blue-100 text-blue-800",
    CANCELLED: "bg-red-100 text-red-700",
};

function TodayBookings({ data, onAction }) {
    if (!data) return <div className="bg-card border border-border rounded-2xl p-5 text-sm text-muted-foreground">Loading…</div>;
    const { counts = {}, rows = [] } = data;
    const total = (counts.PENDING || 0) + (counts.CONFIRMED || 0) + (counts.COMPLETED || 0);
    return (
        <div className="bg-card border border-border rounded-2xl p-5" data-testid="merchant-today-bookings">
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{total} booking{total === 1 ? "" : "s"} today</span>
                {["PENDING", "CONFIRMED", "COMPLETED", "CANCELLED"].map((st) => (
                    counts[st] > 0 && <span key={st} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${PILL[st]}`}>{counts[st]} {st.toLowerCase()}</span>
                ))}
                <Link to="/merchant/bookings" className="ml-auto text-xs font-bold text-primary">View all →</Link>
            </div>
            {rows.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-6">No bookings scheduled for today.</div>
            ) : (
                <ul className="divide-y divide-border">
                    {rows.slice(0, 6).map((b) => (
                        <li key={b.id} className="py-3 flex items-center gap-3" data-testid={`today-row-${b.id}`}>
                            <div className="flex flex-col items-center justify-center w-14 flex-shrink-0">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{new Date(b.scheduled_at).toLocaleString("en-IN", { hour: "numeric", minute: "2-digit", hour12: false }).split(" ")[1] || ""}</span>
                                <span className="text-base font-bold leading-none">{new Date(b.scheduled_at).toLocaleString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm line-clamp-1">{b.product_name}</p>
                                <p className="text-xs text-muted-foreground line-clamp-1">{[b.customer_name, b.customer_mobile, b.duration_min != null && `${b.duration_min} min`].filter(Boolean).join(" · ")}</p>
                            </div>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${PILL[b.status] || "bg-muted"}`}>{b.status}</span>
                            {b.status === "PENDING" && (
                                <button data-testid={`today-confirm-${b.id}`} onClick={() => onAction(b.id, "CONFIRMED")} className="bg-emerald-600 text-white rounded-lg px-2 py-1.5 text-xs font-bold inline-flex items-center gap-1"><Check size={12} /> Confirm</button>
                            )}
                            {b.status === "CONFIRMED" && (
                                <button data-testid={`today-complete-${b.id}`} onClick={() => onAction(b.id, "COMPLETED")} className="bg-primary text-primary-foreground rounded-lg px-2 py-1.5 text-xs font-bold inline-flex items-center gap-1"><CheckCheck size={12} /> Done</button>
                            )}
                        </li>
                    ))}
                    {rows.length > 6 && (
                        <li className="py-3 text-xs text-muted-foreground text-center">+{rows.length - 6} more — <Link to="/merchant/bookings" className="text-primary font-bold">see all</Link></li>
                    )}
                </ul>
            )}
        </div>
    );
}
